import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, Volume2, Activity } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyAudioTestPreview - ÉCRAN 3 : Aperçu Test Audio (détection seuil)
 * Aperçu non interactif du test audiométrique
 */
const JourneyAudioTestPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [activeFreq, setActiveFreq] = useState(0);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  const frequencies = [125, 250, 500, 1000, 2000, 4000, 8000];

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Animation des fréquences (simulation)
    const interval = setInterval(() => {
      setActiveFreq(prev => (prev + 1) % frequencies.length);
    }, 1500);
    
    return () => {
      clearInterval(interval);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('🎧 JourneyAudioTestPreview: Navigating to /journey-tinnitus-test');
    navigate('/journey-tinnitus-test');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(59, 130, 246, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <Headphones className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'The Hearen Audio Test analyzes your perception to adjust Sound to your needs.'
                  : 'Le Test Audio Hearen analyse votre perception afin d\'ajuster le Son à vos besoins.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Aperçu du Test Audio */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-8"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEnglish ? 'Listening Test' : 'Test d\'écoute'}
            </h2>
            <p className="text-blue-400 text-sm flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              {isEnglish ? 'Personalized listening setup' : 'Réglage personnalisé'}
            </p>
          </div>

          {/* Visualisation des fréquences */}
          <div className="mb-6">
            <div className="text-white/70 text-xs text-center mb-3">
              {isEnglish ? 'Sound frequencies analyzed' : 'Fréquences sonores analysées'}
            </div>
            <div className="flex justify-center items-end gap-2">
              {frequencies.map((freq, index) => (
                <div key={freq} className="flex flex-col items-center">
                  <div
                    className="w-8 rounded-t transition-all duration-500"
                    style={{
                      height: index === activeFreq ? '80px' : '40px',
                      background: index === activeFreq 
                        ? 'linear-gradient(to top, rgba(59, 130, 246, 0.8), rgba(59, 130, 246, 0.4))'
                        : 'rgba(59, 130, 246, 0.2)',
                      boxShadow: index === activeFreq ? '0 0 20px rgba(59, 130, 246, 0.6)' : 'none',
                    }}
                  />
                  <span className="text-white/50 text-xs mt-2">
                    {freq >= 1000 ? `${freq / 1000}k` : freq}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sliders désactivés (aperçu) */}
          <div className="space-y-4 opacity-50 mb-6">
            {/* Volume slider */}
            <div>
              <div className="flex items-center justify-between text-xs text-white/70 mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  <span>{isEnglish ? 'Listening intensity' : 'Intensité d\'écoute'}</span>
                </div>
                <span>{isEnglish ? 'Medium' : 'Moyen'}</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: '45%' }}
                />
              </div>
            </div>

            {/* Ear selection */}
            <div className="flex gap-3 justify-center">
              <div className="px-4 py-2 rounded-full bg-blue-500/30 border border-blue-500/50 text-white text-sm">
                {isEnglish ? 'Left' : 'Gauche'}
              </div>
              <div className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/50 text-sm">
                {isEnglish ? 'Right' : 'Droite'}
              </div>
            </div>
          </div>

          {/* Badge Preview */}
          <div className="text-center text-white/50 text-sm">
            {isEnglish ? 'Preview Mode' : 'Mode Aperçu'}
          </div>
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 8px 30px rgba(59, 130, 246, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyAudioTestPreview;
